import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { ToastService } from './toast.service';

enum StorageKeys {
	token = 'token',
	firstLaunch = 'firstLaunch',
}

export interface User {
	userId: string;
	username: string;
	credits: number;
	isAuthenticated: boolean;
}

export interface AuthData {
	username: string;
	email?: string;
	password: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService {
	private apiUrl: string = `${env.ssl ? 'https' : 'http'}://${env.restServer}:${env.restServerPort}`;
	private authUser = new BehaviorSubject<User | null>(null);
	private _token: string | null = null;
	authUser$ = this.authUser.asObservable();

	constructor(
		private http: HttpClient,
		private router: Router,
		private toastService: ToastService,
	) {}

	// ---------------------------------------------------------------------------
	// @ Getters and Setters
	// ---------------------------------------------------------------------------

	get token() {
		return this._token;
	}

	set token(token: string | null) {
		this._token = token;
		if (!token) {
			Preferences.remove({ key: StorageKeys.token });
			return;
		}
		Preferences.set({
			key: StorageKeys.token,
			value: token,
		});
	}

	// ---------------------------------------------------------------------------
	// @ Public Methods
	// ---------------------------------------------------------------------------

	/**
	 * Check if this is the first launch of the app
	 * @returns Promise<boolean> - true if first launch, false otherwise
	 */
	async isFirstLaunch(): Promise<boolean> {
		const { value } = await Preferences.get({ key: StorageKeys.firstLaunch });
		return value !== 'false';
	}

	/**
	 * Mark that the app has been launched (no longer first launch)
	 */
	async markAppLaunched(): Promise<void> {
		await Preferences.set({
			key: StorageKeys.firstLaunch,
			value: 'false',
		});
	}

	/**
	 * @returns The auth status if the auto auth succeeded or failed
	 */
	autoAuthUser(): Observable<User | null> {
		return new Observable(observer => {
			Preferences.get({ key: StorageKeys.token }).then(({ value: token }) => {
				if (!token) {
					observer.next(null);
					observer.complete();
					return;
				}
				this._token = token;

				return this.http
					.get<User>(`${this.apiUrl}/api/user/profile`)
					.pipe(
						tap(user => {
							this.authUser.next(user);
						}),
						catchError(() => {
							observer.next(null);
							observer.complete();
							return of(null);
						}),
					)
					.subscribe({
						next: user => {
							observer.next(user);
							observer.complete();
						},
						error: err => {
							console.error(err);
							observer.next(null);
							observer.complete();
						},
					});
			});
		});
	}

	register(username: string, password: string, email: string) {
		const authData: AuthData = {
			username,
			password,
			email,
		};
		return this.http.post<{ token: string; userId: string }>(`${this.apiUrl}/api/user/register`, authData).pipe(
			tap(({ token }) => {
				this.token = token;

				const message = 'Benutzer wurde angelegt.';
				this.toastService.showToastOnSuccess(message);
			}),
		);
	}

	login(credentials: { username: string; password: string }) {
		return this.http.post<{ userId: string; token: string }>(`${this.apiUrl}/api/user/login`, credentials).pipe(
			tap((data: { userId: string; token: string }) => {
				this.token = data.token;
			}),
		);
	}

	// Google-OAuth
	loginWithGoogle() {
		window.location.href = `${this.apiUrl}/api/auth/google`;
	}

	logout() {
		this.authUser.next(null);
		this.token = null;
		this.clearAuthData();
		this.router.navigate(['']);
	}

	// User reset password
	requestPassword(username: string) {
		return this.http.post<{ msg: 'success' }>(`${this.apiUrl}/api/user/resetPassword`, { username });
	}

	updatePassword(resetToken: string, newPassword: string) {
		return this.http.post(`${this.apiUrl}/api/user/updateRecoveryPassword`, {
			resetToken,
			newPassword,
		});
	}

	getUserProfile() {
		return this.http.get<User>(`${this.apiUrl}/api/user/profile`).pipe(
			tap(user => {
				this.authUser.next(user);
			}),
		);
	}

	updateUsername(username: string) {
		return this.http.put<{ msg: 'success' }>(`${this.apiUrl}/api/user/me/username`, { username });
	}

	updateUserCredits(newCredits: number) {
		const currentUser = this.authUser.value;
		if (currentUser) {
			this.authUser.next({ ...currentUser, credits: newCredits });
		}
	}

	deleteUser() {
		return this.http.delete<{ msg: 'success' }>(`${this.apiUrl}/api/user/me`);
	}

	private clearAuthData() {
		Preferences.remove({ key: StorageKeys.token });
	}
}
